import { ApiResponseWrapper, isErrorResponse } from "@/types/api";
import { resolveApiUrl } from './resolveApiUrl';

// Identity API specific request handler
async function parseIdentityApiResponse<T>(res: Response): Promise<ApiResponseWrapper<T>> {
  try {
    const jsonData: ApiResponseWrapper<T> = await res.json();
    
    if (isErrorResponse(jsonData)) {
      const errorMessage = jsonData.error?.message || res.statusText || 'An error occurred';
      const error = new Error(errorMessage);
      (error as any).status = res.status;
      (error as any).statusText = res.statusText;
      (error as any).errorCode = jsonData.error?.code;
      (error as any).errorDetails = jsonData.error?.details;
      (error as any).fullResponse = jsonData;
      
      throw error;
    }
    
    return jsonData;
  } catch (parseError) {
    if (parseError instanceof Error && (parseError as any).fullResponse) {
      throw parseError;
    }
    
    const error = new Error('Invalid API response: Unable to parse JSON');
    (error as any).status = res.status;
    (error as any).statusText = res.statusText;
    throw error;
  }
}

// Identity API request handler
export async function identityApiRequest<T = any>(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<ApiResponseWrapper<T>> {
  // Build full URL with identity API base URL
  // Runtime config takes precedence, then build-time env; resolveApiUrl owns the last step
  // (dev default in a DEV build, a loud throw in PROD).
  const baseUrl = resolveApiUrl(
    window.APP_CONFIG?.IDENTITY_API_BASE_URL || import.meta.env.VITE_IDENTITY_API_BASE_URL,
    'https://identity.api.dev.athlyn.app',
    'VITE_IDENTITY_API_BASE_URL',
  );
  const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;

  const headers: Record<string, string> = {};

  // Add Content-Type for requests with data
  if (data) {
    headers["Content-Type"] = "application/json";
  }

  const res = await fetch(fullUrl, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
  });

  return await parseIdentityApiResponse<T>(res);
}

// Extract data from identity API response
export async function fetchIdentityApiData<T>(method: string, url: string, data?: unknown): Promise<T> {
  const response = await identityApiRequest<T>(method, url, data);
  
  // Extract the data from the wrapper
  if ('data' in response) {
    return (response as any).data;
  }
  
  throw new Error('Invalid API response: no data received');
}